/**
 * Maps the backend `GET /auth/me` profile onto the app's `AuthUser`.
 *
 * The backend's three roles (see swagger/roles.json) map onto the
 * customer roles from documents/architecture/roles.md:
 *
 *   Admin              → UserRole.AdminUser
 *   Compliance Officer → UserRole.ComplianceOfficer
 *   Standard User      → UserRole.StandardUser
 *
 * The AMLiQ super admin (UserRole.SystemAdmin) is the user with no tenant:
 * `tenant === null` on the `/auth/me` profile marks an internal, cross-account
 * admin. Tenant users carry one or more backend roles that map onto the
 * customer roles; the app's single RBAC role is the highest-privileged of
 * them, and no/unknown roles fall back to the least-privileged Standard user.
 */
import type { AuthUser, BackendRole, BackendRoleName, MeProfile } from '../types'
import { ROLE_LEVEL, UserRole } from '@/lib/rbac'

/** Backend role name → app role. */
const BACKEND_ROLE_TO_APP_ROLE: Record<BackendRoleName, UserRole> = {
  Admin: UserRole.AdminUser,
  'Compliance Officer': UserRole.ComplianceOfficer,
  'Standard User': UserRole.StandardUser,
}

/**
 * Collapse the backend's role assignments into the single app role the RBAC
 * layer expects: the highest-privileged role assigned (by `ROLE_LEVEL`).
 * Falls back to Standard user when no role (or only unknown names) is present.
 */
function highestAppRole(roles: Array<BackendRole>): UserRole {
  let best: UserRole = UserRole.StandardUser
  for (const { name } of roles) {
    // An unknown name (a contract violation) maps to `undefined`, whose level
    // comparison is `false` — so it's skipped and the fallback stands.
    const mapped = BACKEND_ROLE_TO_APP_ROLE[name]
    if (ROLE_LEVEL[mapped] > ROLE_LEVEL[best]) best = mapped
  }
  return best
}

/**
 * Map every backend role assignment to its app role (deduped). Drives
 * multi-role authorization so a user who is e.g. both Admin and Compliance
 * Officer satisfies `hasRole` for each — not just their highest role.
 */
function appRoles(roles: Array<BackendRole>): Array<UserRole> {
  return [...new Set(roles.map(({ name }) => BACKEND_ROLE_TO_APP_ROLE[name]))]
}

/** Convert the raw `/auth/me` payload into the app's `AuthUser`. */
export function mapProfileToAuthUser(profile: MeProfile): AuthUser {
  // No tenant → the AMLiQ super admin (platform-level, cross-account).
  if (!profile.tenant) {
    return {
      id: profile.id,
      email: profile.email,
      fullName: profile.name ?? profile.email,
      role: UserRole.SystemAdmin,
      roles: [UserRole.SystemAdmin],
      roleNames: [],
      accountId: null,
      accountSector: null,
    }
  }

  // Tenant user → backend roles collapsed to the app role, scoped to the tenant.
  // The raw role names are kept too (a user can hold several, e.g. Admin + CO).
  return {
    id: profile.id,
    email: profile.email,
    fullName: profile.name ?? profile.email,
    role: highestAppRole(profile.roles),
    roles: appRoles(profile.roles),
    roleNames: profile.roles.map((r) => r.name),
    accountId: profile.tenant.id,
    accountSector: profile.tenant.sector?.name ?? null,
  }
}
